import { Subject } from 'rxjs'
import { mergeMap } from 'rxjs/operators'

import { EntityType, EntitySynchroResult } from '@skinsoft/types';
import { SynchroUpHandler } from "@skinsoft/classes-interfaces-definition";
import { MediaUploadHandler } from './media-upload-handler';
import { OfflineDataUpdateCacher } from '@skinsoft/offline';

interface EntityInfos {
    id: string,
    nbEntityToWait: number,
    syncUpHandler: SynchroUpHandler,
}

declare const config
const MAX_SYNC_SAME_TIME = config.syncUp.strategy.maxNbSyncParallel
const SECURITY_DELAY_BEFORE_CREATION = config.syncUp.strategy.creationSecurityDelay // prevents entities to be created with the same inventory number
const PICK_NEXT_ENTITIES_DELAY = config.syncUp.strategy.pickNextEntitiesDelay

export class SynchroUpStrategy {
    
    private syncUpHandlers: SynchroUpHandler[]
    private readyToSync: { id: string, syncUpHandler: SynchroUpHandler }[]
    private waitingEntities: EntityInfos[]
    private idsWaitMap: { [idToWait: string]: string[] }
    private idTypeMap: { [idEntity: string]: EntityType }

    private syncNewEntitySubject: Subject<{ id: string, syncUpHandler: SynchroUpHandler }>

    constructor(syncUpHandlers: SynchroUpHandler[]) {
        this.syncUpHandlers = syncUpHandlers
        this.syncNewEntitySubject = new Subject()
        this.waitingEntities = []
        this.idsWaitMap = {}
        this.readyToSync = []
        this.idTypeMap = {}
    }

    public async run(): Promise<Subject<EntitySynchroResult>> {
        const theRealThis = this

        const entitySyncedSubject: Subject<EntitySynchroResult> = new Subject()

        const entitiesFailedBeforeSync: string[] = []

        // get cached ids dependencies
        console.debug("Getting cached entities dependencies...")
        for(const syncUpHandler of this.syncUpHandlers) {
            const syncUpHandlerType = syncUpHandler.getType()
            const allIds = await syncUpHandler.getCachedObjectsIds().toPromise()

            for(const idEntity of allIds) {
                this.idTypeMap[idEntity] = syncUpHandlerType

                const dependenciesIds: string[] = []
                try {
                    const dependencies = await syncUpHandler.getDependenciesIds(idEntity).toPromise()
                    for(const dependency of dependencies) {
                        dependenciesIds.push(dependency.id)
                        this.idTypeMap[dependency.id] = dependency.type
                    }
                    console.debug(`Dependencies of ${idEntity}: `, dependenciesIds)
                } catch(err) {
                    entitiesFailedBeforeSync.push(idEntity)
                    console.error(`Failed to get dependencies of ${idEntity}`)
                    console.log(err)
                    continue
                }

                this.waitingEntities.push({
                    id: idEntity,
                    nbEntityToWait: dependenciesIds.length,
                    syncUpHandler: syncUpHandler,
                })

                for(const dependencyId of dependenciesIds) {
                    if(this.idsWaitMap[dependencyId]) {
                        this.idsWaitMap[dependencyId].push(idEntity)
                    } else {
                        this.idsWaitMap[dependencyId] = [idEntity]
                    }
                }
            }
        }

        console.debug("Waiting entities: ", theRealThis.waitingEntities)

        this.syncNewEntitySubject.pipe(
            mergeMap(entityToSync => {
                console.debug(`SyncUpStrategy syncNewEntitySubject: ${entityToSync.id}`)
                return entityToSync.syncUpHandler.syncUp(entityToSync.id)
            })
        ).subscribe((res) => {
            entitySyncedSubject.next({
                success: res[0],
                entityId: res[1],
                entityType: theRealThis.idTypeMap[res[1]]
            })
        })

        entitySyncedSubject.subscribe((synchroResult) => {
            console.debug(`SyncUpStrategy entitySyncedSubject: ${synchroResult.entityId}, ${synchroResult.success}`)

            theRealThis.readyToSync = this.readyToSync.filter(entity => {
                return entity.id !== synchroResult.entityId
            })
            console.debug("SyncUpStrategy entitySyncedSubject, readyToSync: ", theRealThis.readyToSync)

            const dependentsIds = theRealThis.idsWaitMap[synchroResult.entityId]
            console.debug("SyncUpStrategy entitySyncedSubject, dependentsIds: ", dependentsIds)
            if(dependentsIds?.length) {
                for(const dependentId of dependentsIds) {
                    const dependentEntity = theRealThis.waitingEntities.find(waitingEntity => {
                        return waitingEntity.id === dependentId
                    })
                    if(dependentEntity) {
                        if(synchroResult.success) {
                            console.log(`SyncUpStrategy entitySyncedSubject, updating nbEntityToWait: `, dependentEntity)
                            dependentEntity.nbEntityToWait--
                        } else {
                            entitySyncedSubject.next({
                                success: false, 
                                entityId: dependentEntity.id,
                                entityType: theRealThis.idTypeMap[dependentEntity.id],
                            })
                            theRealThis.waitingEntities = theRealThis.waitingEntities.filter(
                                waitingEntity => {
                                    return waitingEntity.id !== dependentEntity.id    
                                }
                            )
                        }
                    }
                }
            }
        })

        setTimeout(() => {
            console.debug("Starting SYNC-UP...")
            console.debug("Waiting entities: ", theRealThis.waitingEntities)
            console.debug("Ids wait map: ", theRealThis.idsWaitMap)
            console.debug("Ready to sync: ", theRealThis.readyToSync)

            for(const idEntity of entitiesFailedBeforeSync) {
                entitySyncedSubject.next({
                    success: false, 
                    entityId: idEntity,
                    entityType: theRealThis.idTypeMap[idEntity],
                })
            }
    
            new MediaUploadHandler().run(entitySyncedSubject)
            theRealThis.pickNextEntities()
        })


        return entitySyncedSubject
    }

    private pickNextEntities() {
        const theRealThis = this

        if(this.waitingEntities.length > 0) {
            if(this.readyToSync.length < MAX_SYNC_SAME_TIME) {
                const nbElemToSync = MAX_SYNC_SAME_TIME - this.readyToSync.length
    
                const selectedEntities: EntityInfos[] = this.waitingEntities.filter(waitingEntity => {
                    return waitingEntity.nbEntityToWait === 0
                }).slice(0, nbElemToSync)

                console.debug("pickNextEntities: Entity ready for sync up: ", selectedEntities)
    
                if(selectedEntities.length) {
                    this.waitingEntities = this.waitingEntities.filter(waitingEntity => {
                        return !selectedEntities.find(selectedEntity => selectedEntity.id === waitingEntity.id)
                    })
    
                    const entityToSync = selectedEntities.map(selectedEntity => {
                        return {
                            id: selectedEntity.id,
                            syncUpHandler: selectedEntity.syncUpHandler
                        }
                    })
    
                    this.readyToSync.push(...entityToSync)
                    console.log("pickNextEntities: readyToSync: ", this.readyToSync)
    
                    const nbCreation = {
                        [EntityType.AUDIT_CAMPAIGN]: 0,
                        [EntityType.AUDIT_RECORD]: 0,
                        [EntityType.CONDITION_REPORT]: 0,
                        [EntityType.WEMI]: 0,
                        [EntityType.MEDIA]: 0,
                    }
                    for(const entity of entityToSync) {
                        const isCreation = OfflineDataUpdateCacher.isTemporaryId(entity.id)
                        
                        if(isCreation) {
                            // if we create a new entity, we need a little delay between the creation
                            // of each entities of the same type to prevent creating entities with the same
                            // inventory number 
                            const type = entity.syncUpHandler.getType()
                            setTimeout(
                                () => {
                                    theRealThis.syncNewEntitySubject.next(entity)
                                }, 
                                nbCreation[type] * SECURITY_DELAY_BEFORE_CREATION
                            )
                            nbCreation[type]++

                        } else {
                            theRealThis.syncNewEntitySubject.next(entity)
                        }
                    }
                }

            } else {
                console.debug("pickNextEntities: No entities to sync")
            }
            
            setTimeout(() => {
                theRealThis.pickNextEntities()
            }, PICK_NEXT_ENTITIES_DELAY)
        }
    }
}